/**
 * Pure geometry + a11y text for the seek bar. Extracted so it can be tested:
 * the repo has no DOM test environment, and Radix's own slider markup is
 * derived in layout effects (it is not assertable under renderToStaticMarkup).
 */

export type BufferedSegment = { leftPct: number; widthPct: number };

/**
 * Turn a media element's `buffered` TimeRanges (already flattened to tuples)
 * into percentage bars. Ranges are clamped to the duration, dropped when empty,
 * sorted, and overlapping/adjacent ones merged so the layer never double-paints.
 */
export function bufferedSegments(
  ranges: ReadonlyArray<readonly [number, number]>,
  duration: number,
): BufferedSegment[] {
  if (!Number.isFinite(duration) || duration <= 0) return [];
  const clamped = ranges
    .map(
      ([start, end]) =>
        [Math.max(0, Math.min(start, duration)), Math.max(0, Math.min(end, duration))] as const,
    )
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0]);

  const merged: Array<[number, number]> = [];
  for (const [start, end] of clamped) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged.map(([start, end]) => ({
    leftPct: (start / duration) * 100,
    widthPct: ((end - start) / duration) * 100,
  }));
}

/** "3 minutes 20 seconds" — spoken form, for aria-valuetext (never the label). */
export function spokenDuration(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} hour${h === 1 ? '' : 's'}`);
  if (m) parts.push(`${m} minute${m === 1 ? '' : 's'}`);
  if (s || parts.length === 0) parts.push(`${s} second${s === 1 ? '' : 's'}`);
  return parts.join(' ');
}

/** `aria-valuetext` for the scrubber: position spoken against the total. */
export function scrubberValueText(current: number, duration: number): string {
  if (!Number.isFinite(duration) || duration <= 0) return spokenDuration(current);
  return `${spokenDuration(current)} of ${spokenDuration(duration)}`;
}

/**
 * Where the watched region ends, as a percentage — the shaded boundary the
 * scrubber paints when seeking is restricted. Null when there is nothing to
 * show: no limit, an unknown duration, or a limit already past the end.
 */
export function seekLimitPct(limit: number | null | undefined, duration: number): number | null {
  if (limit == null || !Number.isFinite(limit) || limit < 0) return null;
  if (!Number.isFinite(duration) || duration <= 0) return null;
  if (limit >= duration) return null;
  return (limit / duration) * 100;
}

/** `aria-valuetext`, extended to say where the seekable region stops. */
export function scrubberValueTextWithLimit(
  current: number,
  duration: number,
  limit: number | null | undefined,
): string {
  const base = scrubberValueText(current, duration);
  const pct = seekLimitPct(limit, duration);
  if (pct == null) return base;
  return `${base}. Watched up to ${spokenDuration(limit as number)}; you cannot skip past this yet.`;
}
